import React, { useState, useEffect, useContext } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { AuthContext } from "@/App";
import { useLanguage } from "@/contexts/LanguageContext";
import { translate } from "@/utils/translations";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Cpu,
  Activity,
  CheckCircle,
  Coins,
  Wifi,
  Clock,
  TrendingUp,
  ArrowUpRight,
  ArrowDownRight,
  Brain,
  Sparkles,
  ArrowRight
} from "lucide-react";
import {
  LineChart,
  Line,
  AreaChart,
  Area,
  BarChart,
  Bar,
  PieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Legend
} from "recharts";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Dashboard = () => {
  const { user } = useContext(AuthContext);
  const location = useLocation();
  const navigate = useNavigate();
  const { language } = useLanguage();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [telemetry, setTelemetry] = useState(null);

  const t = (text) => translate(text, language);
  const isDemo = new URLSearchParams(location.search).get("demo") === "true";

  const goToCerebro = () => {
    navigate(isDemo ? "/cerebro?demo=true" : "/cerebro");
  };

  useEffect(() => {
    fetchStats();
    
    // WebSocket for real-time telemetry
    const wsUrl = BACKEND_URL.replace('https://', 'wss://').replace('http://', 'ws://');
    let ws;
    
    try {
      ws = new WebSocket(`${wsUrl}/ws/telemetry`);
      
      ws.onmessage = (event) => {
        const data = JSON.parse(event.data);
        setTelemetry(data);
      };
      
      ws.onerror = () => {
        console.log("WebSocket not available, using polling");
      };
    } catch (e) {
      console.log("WebSocket connection failed");
    }

    return () => {
      if (ws && ws.readyState === WebSocket.OPEN) {
        ws.close();
      }
    };
  }, [isDemo]);

  const fetchStats = async () => {
    try {
      const res = await fetch(`${API}/stats/dashboard${isDemo ? '?demo=true' : ''}`, {
        credentials: "include"
      });
      const data = await res.json();
      setStats(data);
    } catch (e) {
      console.error("Failed to fetch stats:", e);
    } finally {
      setLoading(false);
    }
  };

  const kpis = stats ? [
    { 
      label: t("Active Robots"), 
      value: stats.active_robots, 
      icon: Cpu, 
      change: "+12%",
      positive: true,
      color: "cyan"
    },
    { 
      label: t("Inference Jobs"), 
      value: stats.inference_jobs?.toLocaleString(), 
      icon: Activity, 
      change: "+8.3%",
      positive: true,
      color: "green"
    },
    { 
      label: t("Verified Actions"), 
      value: stats.verified_actions?.toLocaleString(), 
      icon: CheckCircle, 
      change: "+15%",
      positive: true,
      color: "purple"
    },
    { 
      label: t("Compute Balance"), 
      value: stats.compute_balance?.toFixed(2), 
      icon: Coins, 
      change: "-2.1%",
      positive: false,
      color: "yellow"
    },
    { 
      label: t("Network Uptime"), 
      value: `${stats.network_uptime}%`, 
      icon: Wifi, 
      change: "+0.02%",
      positive: true,
      color: "green"
    },
    { 
      label: t("Latency"), 
      value: `${stats.latency_ms?.toFixed(1)}ms`, 
      icon: Clock, 
      change: "-5ms",
      positive: true,
      color: "cyan"
    },
    { 
      label: t("Learning Rate"), 
      value: stats.learning_rate?.toFixed(4), 
      icon: TrendingUp, 
      change: "Stable",
      positive: true,
      color: "blue"
    }
  ] : [];

  const COLORS = ['#00D2FF', '#00FF94', '#3399FF', '#FFB800', '#FF003C'];

  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      return (
        <div className="glass rounded-lg p-3 text-xs">
          <p className="text-gray-400 mb-1">{label}</p>
          {payload.map((entry, idx) => (
            <p key={idx} style={{ color: entry.color }} className="font-mono">
              {entry.name}: {entry.value}
            </p>
          ))}
        </div>
      );
    }
    return null;
  };

  if (loading) {
    return (
      <div className="space-y-6" data-testid="dashboard-loading">
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-4">
          {[...Array(7)].map((_, i) => (
            <Skeleton key={i} className="h-24 bg-white/5" />
          ))}
        </div>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <Skeleton className="h-80 bg-white/5" />
          <Skeleton className="h-80 bg-white/5" />
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="dashboard">
      {/* Try Cerebro CTA - Prominent Box */}
      <Card 
        className="bg-gradient-to-r from-[#00D2FF]/10 via-[#3399FF]/10 to-[#00D2FF]/10 border-[#00D2FF]/30 cursor-pointer hover:border-[#00D2FF]/50 transition-all group overflow-hidden relative"
        onClick={goToCerebro}
        data-testid="try-cerebro-cta"
      >
        <div className="absolute inset-0 bg-gradient-to-r from-[#00D2FF]/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity" />
        <CardContent className="p-6 flex items-center gap-6">
          <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-[#00D2FF] to-[#3399FF] flex items-center justify-center flex-shrink-0 group-hover:scale-105 transition-transform">
            <Brain className="w-8 h-8 text-black" />
          </div>
          <div className="flex-1">
            <div className="flex items-center gap-2 mb-1">
              <h2 className="font-heading text-xl font-bold text-white">{t("Try Cerebro")}</h2>
              <Badge className="bg-[#00FF94]/20 text-[#00FF94] border-0 text-[10px]">
                <Sparkles className="w-3 h-3 mr-1" />
                OPERATIONAL
              </Badge>
            </div>
            <p className="text-gray-400 text-sm">
              {t("Your intelligent assistant with 10 specialized modes — from Executive Advisory to Creative Brainstorming. Ask anything.")}
            </p>
          </div>
          <Button 
            className="bg-[#00D2FF] hover:bg-[#33e0ff] text-black font-bold px-6 group-hover:shadow-[0_0_20px_rgba(0,210,255,0.4)] transition-all"
          >
            {t("Open Cerebro")}
            <ArrowRight className="w-4 h-4 ml-2" />
          </Button>
        </CardContent>
      </Card>

      {/* Welcome header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-heading text-2xl lg:text-3xl font-bold text-white">
            {t("Welcome back")}, {user?.name?.split(' ')[0] || 'Operator'}
          </h1>
          <p className="text-gray-500 text-sm mt-1">
            {t("Here's your network overview for today")}
          </p>
        </div>
        {telemetry && (
          <Badge className="bg-[#00FF94]/10 text-[#00FF94] border-[#00FF94]/20 font-mono text-xs">
            <span className="w-2 h-2 rounded-full bg-[#00FF94] mr-2 animate-pulse" />
            LIVE
          </Badge>
        )}
      </div>

      {/* KPI Grid - Bento style */}
      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-4">
        {kpis.map((kpi, idx) => (
          <Card 
            key={kpi.label} 
            className="bg-[#0A0A0A] border-white/10 hover:border-cyan-500/30 transition-colors"
            style={{ animationDelay: `${idx * 0.05}s` }}
          >
            <CardContent className="p-4">
              <div className="flex items-center justify-between mb-3">
                <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${
                  kpi.color === 'cyan' ? 'bg-[#00D2FF]/10' :
                  kpi.color === 'green' ? 'bg-[#00FF94]/10' :
                  kpi.color === 'purple' ? 'bg-[#3399FF]/10' :
                  kpi.color === 'yellow' ? 'bg-yellow-500/10' :
                  'bg-[#00D2FF]/10'
                }`}>
                  <kpi.icon className={`w-4 h-4 ${
                    kpi.color === 'cyan' ? 'text-[#00D2FF]' :
                    kpi.color === 'green' ? 'text-[#00FF94]' :
                    kpi.color === 'purple' ? 'text-[#3399FF]' :
                    kpi.color === 'yellow' ? 'text-yellow-400' :
                    'text-[#00D2FF]'
                  }`} />
                </div>
                <span className={`text-xs flex items-center gap-0.5 ${
                  kpi.positive ? 'text-[#00FF94]' : 'text-red-400'
                }`}>
                  {kpi.positive ? <ArrowUpRight className="w-3 h-3" /> : <ArrowDownRight className="w-3 h-3" />}
                  {kpi.change}
                </span>
              </div>
              <p className="text-xl font-bold text-white font-mono">{kpi.value}</p>
              <p className="text-xs text-gray-500 mt-1">{kpi.label}</p>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Charts row */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Throughput chart */}
        <Card className="bg-[#0A0A0A] border-white/10">
          <CardHeader className="pb-2">
            <CardTitle className="font-heading text-lg text-white">{t("Training Throughput")}</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="h-64">
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={stats?.charts?.throughput || []}>
                  <defs>
                    <linearGradient id="colorInference" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#00D2FF" stopOpacity={0.3}/>
                      <stop offset="95%" stopColor="#00D2FF" stopOpacity={0}/>
                    </linearGradient>
                    <linearGradient id="colorTraining" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#3399FF" stopOpacity={0.3}/>
                      <stop offset="95%" stopColor="#3399FF" stopOpacity={0}/>
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="#1F1F1F" />
                  <XAxis dataKey="time" stroke="#52525B" tick={{ fontSize: 10 }} />
                  <YAxis stroke="#52525B" tick={{ fontSize: 10 }} />
                  <Tooltip content={<CustomTooltip />} />
                  <Area 
                    type="monotone" 
                    dataKey="inference" 
                    stroke="#00D2FF" 
                    fillOpacity={1} 
                    fill="url(#colorInference)" 
                    name="Inference"
                  />
                  <Area 
                    type="monotone" 
                    dataKey="training" 
                    stroke="#3399FF" 
                    fillOpacity={1} 
                    fill="url(#colorTraining)" 
                    name="Training"
                  />
                </AreaChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>

        {/* Agent usage pie chart */}
        <Card className="bg-[#0A0A0A] border-white/10">
          <CardHeader className="pb-2">
            <CardTitle className="font-heading text-lg text-white">{t("Agent Usage Distribution")}</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="h-64 flex items-center">
              <ResponsiveContainer width="50%" height="100%">
                <PieChart>
                  <Pie
                    data={stats?.charts?.agent_usage || []}
                    cx="50%"
                    cy="50%"
                    innerRadius={50}
                    outerRadius={80}
                    paddingAngle={2}
                    dataKey="value"
                  >
                    {(stats?.charts?.agent_usage || []).map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip content={<CustomTooltip />} />
                </PieChart>
              </ResponsiveContainer>
              <div className="flex-1 space-y-2">
                {(stats?.charts?.agent_usage || []).map((entry, idx) => (
                  <div key={entry.name} className="flex items-center gap-2">
                    <span 
                      className="w-3 h-3 rounded-sm" 
                      style={{ backgroundColor: COLORS[idx % COLORS.length] }}
                    />
                    <span className="text-sm text-gray-400 flex-1">{entry.name}</span>
                    <span className="text-sm font-mono text-white">{entry.value}%</span>
                  </div>
                ))}
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Bottom row */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Rewards chart */}
        <Card className="lg:col-span-2 bg-[#0A0A0A] border-white/10">
          <CardHeader className="pb-2">
            <CardTitle className="font-heading text-lg text-white">{t("Rewards This Week")}</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="h-48">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={stats?.charts?.rewards || []}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#1F1F1F" />
                  <XAxis dataKey="day" stroke="#52525B" tick={{ fontSize: 10 }} />
                  <YAxis stroke="#52525B" tick={{ fontSize: 10 }} />
                  <Tooltip content={<CustomTooltip />} />
                  <Bar dataKey="earned" fill="#00FF94" radius={[4, 4, 0, 0]} name="Earned" />
                  <Bar dataKey="spent" fill="#FF003C" radius={[4, 4, 0, 0]} name="Spent" />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>

        {/* Node distribution */}
        <Card className="bg-[#0A0A0A] border-white/10">
          <CardHeader className="pb-2">
            <CardTitle className="font-heading text-lg text-white">{t("Node Distribution")}</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {(stats?.charts?.node_distribution || []).map((region) => (
                <div key={region.region} className="space-y-1">
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-gray-400">{t(region.region)}</span>
                    <span className="font-mono text-white">{region.count}</span>
                  </div>
                  <div className="h-2 bg-white/5 rounded-full overflow-hidden">
                    <div 
                      className="h-full bg-gradient-to-r from-[#00D2FF] to-[#3399FF] rounded-full"
                      style={{ width: `${(region.count / 5) * 100}%` }}
                    />
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Live telemetry panel */}
      {telemetry && (
        <Card className="bg-[#0A0A0A] border-[#00D2FF]/30 glow-blue">
          <CardHeader className="pb-2">
            <CardTitle className="font-heading text-lg text-white flex items-center gap-2">
              <span className="w-2 h-2 rounded-full bg-[#00D2FF] animate-pulse" />
              Live Network Telemetry
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-3 gap-6 font-mono text-sm">
              <div>
                <p className="text-gray-500 mb-1">Nodes</p>
                <p className="text-[#00FF94]">Online: {telemetry.nodes?.online}</p>
                <p className="text-red-400">Offline: {telemetry.nodes?.offline}</p>
                <p className="text-yellow-400">Syncing: {telemetry.nodes?.syncing}</p>
              </div>
              <div>
                <p className="text-gray-500 mb-1">Inference</p>
                <p className="text-white">Rate: {telemetry.inference?.rate?.toFixed(1)}/s</p>
                <p className="text-white">Queue: {telemetry.inference?.queue}</p>
                <p className="text-white">Latency: {telemetry.inference?.latency?.toFixed(1)}ms</p>
              </div>
              <div>
                <p className="text-gray-500 mb-1">Network</p>
                <p className="text-white">BW: {telemetry.network?.bandwidth?.toFixed(1)} MB/s</p>
                <p className="text-white">Packets: {telemetry.network?.packets?.toLocaleString()}</p>
                <p className="text-white">Errors: {telemetry.network?.errors}</p>
              </div>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default Dashboard;
